import React, {Component} from 'react';
import equals from 'fast-deep-equal';
import shortid from 'shortid';
import * as Sentry from "@sentry/browser";
import segment from "segment";

import Config from 'config.js';

import { EventSourcePolyfill } from 'event-source-polyfill';
import Cookies from 'js-cookie';

import {Context} from "context/index"
import {ping} from "repository/Api";
import * as FolderStructure from "util/FolderStructure";
import * as Windows from 'util/Windows';

import hints from 'text/hints';
import groupNames from 'text/groupNames';
import shortcuts from 'text/shortcuts';
import copy from 'text/copy'
import * as ProjectController from "controller/ProjectController";
import * as UserController from "controller/UserController";
import * as ProjectMapController from "controller/MapController";
import * as UIController from "controller/UIController";
import * as TicketController from "controller/TicketController";

const url = new URL(window.location);
const projectId = url.pathname.split("/")[1];
const mapPack = url.searchParams.get("mapPack");

export default class ContextProvider extends Component {
    state = {
        //user
        user: {},
        lightTheme: false,
        setLightTheme: this.setLightTheme.bind(this),

        //mercure
        setEventSource: this.setEventSource,
        addEventSourceTopic: this.addEventSourceTopic,
        addEventSourceListener: this.addEventSourceListener,
        removeEventSourceTopic: this.removeEventSourceTopic,
        removeEventSourceListener: this.removeEventSourceListener,
        mercureLogin: this.mercureLogin,

        //project
        id: "",
        mapPack: "",
        mapPacks: [],
        metadata: {},
        folderStructure: [],
        calibrationData: "",
        modifiedCalibrationData: "",
        loadedMaps: {},
        setMapModified: this.setMapModified.bind(this),
        setMapModifiedAxis: this.setMapModifiedAxis.bind(this),
        toggleMapFavourited: this.toggleMapFavourited.bind(this),
        undo: this.undo.bind(this),

        //ui
        loaded: false,
        text: {copy, hints, groupNames, shortcuts},
        windows: [],
        toasts: [],
        touch: false,
        networkConnected: true,
        blackout: false,
        disclaimerConfirmed: false,
        fullScreen: false,
        updateWindowPos: this.updateWindowPos.bind(this),
        activateAxisEditing: this.activateAxisEditing.bind(this),
        deactivateAxisEditing: this.deactivateAxisEditing.bind(this),
        openWindow: this.openWindow.bind(this),
        closeWindow: this.closeWindow.bind(this),
        setSelectedWindow: this.setSelectedWindow.bind(this),
        getSelectedWindow: this.getSelectedWindow.bind(this),
        confirmDisclaimer: this.confirmDisclaimer.bind(this),
        toggleFullScreen: this.toggleFullScreen.bind(this),
        setFullScreen: this.setFullScreen.bind(this),
        alert: this.alert.bind(this),
        confirm: this.confirm.bind(this),
        unsetToast: this.unsetToast.bind(this),
        reportMapIssue: this.reportMapIssue.bind(this)
    };

    eventSource = null;
    eventSourceTopics = [];

    async componentDidMount() {
        try {
            const user = await UserController.getUser();
            const project = await ProjectController.getProject(projectId, mapPack);
            let modifiedCalibrationData = await ProjectController.getModifiedCalibrationData(projectId);
            await this.mercureLogin();
            if(Cookies.get("mercureToken")) {
                this.subscribeModifiedCalibrationData(modifiedCalibrationData.id);
            }
            modifiedCalibrationData = modifiedCalibrationData.data;
            const UI = UIController.getUI(projectId);

            //make sure that only maps in current folder structure are opened - deals with differences between map packs
            const folderStructureMaps = project.folderStructure.map(folder => folder.codeNames.map(map => map.id)).flat();
            UI.windows = UI.windows.filter(window => folderStructureMaps.includes(window.mapId));

            const openMapIds = UI.windows.map(window => window.mapId);
            const loadedMaps = await ProjectMapController.getMaps(openMapIds);

            this.setState({user, ...project, ...UI, loaded: true, loadedMaps, modifiedCalibrationData});
            document.title = `${project.metadata.projectName} - Speedweaver`;

            await segment.track("project_opened", {
                application: "editor",
                projectId: this.state.id,
                projectType: this.state.metadata.projectType === "DEMO" ? "demo" : "user",
            })

            await this._checkNetworkConnection();
            window.addEventListener('online', this._checkNetworkConnection.bind(this));
            window.addEventListener('offline', this._checkNetworkConnection.bind(this));
        } catch (e) {
            console.error(e);
            console.log(e)
            Cookies.remove('mercureToken');
            Sentry.captureException(e);
            window.location = Config.dashboard
        }
    }

    setLightTheme(theme){
        this.setState({
            lightTheme: theme
        })
    }

    // mercure

    setEventSource() {
        if(this.eventSource !== null) this.eventSource.close();
        if(this.eventSourceTopics.length < 1) {
            this.eventSource = null;

            return Promise.resolve();
        }
        let mercureUrl = new URL(Config.mercureURL);
        this.eventSourceTopics.forEach((topic, idx) => {
            mercureUrl.searchParams.append("topic", `${Config.api}${topic}`);
        });

        let eventSource = new EventSourcePolyfill(mercureUrl, {
            headers: {
                Authorization: `Bearer ${Cookies.get('mercureToken')}`
            },
        });

        if(this.eventSource !== null) eventSource._listeners = this.eventSource._listeners;

        return new Promise((resolve, reject) => {
            eventSource.onopen = () => {
                this.eventSource = eventSource;
                resolve(eventSource)
            };

            eventSource.onerror = reject;
        });
    }

    async addEventSourceTopic(topic) {
        try {
            if(this.eventSourceTopics.includes(topic)) return this.eventSource;
            this.eventSourceTopics = [...this.eventSourceTopics, topic];
            await this.setEventSource();
        } catch(error) {
            Cookies.remove("mercureToken");
        }
    }

    addEventSourceListener(channel, func) {
        try {
            if(!this.eventSource) throw new Error("EventSource is null");
            this.eventSource.addEventListener(channel, func)
        } catch(error) {
            Cookies.remove('mercureToken');
        }
    }

    async removeEventSourceTopic(topic) {
        if(!this.eventSourceTopics.includes(topic)) return this.eventSource;
        this.eventSourceTopics = this.eventSourceTopics.filter(stateTopic => stateTopic !== topic);
        await this.setEventSource();
    }

    removeEventSourceListener(channel, func) {
        if(!this.eventSource) return;
        this.eventSource.removeEventListener(channel, func)
    }

    async subscribeModifiedCalibrationData(id) {
        await this.addEventSourceTopic(`/project_calibration_datas/${id}`);
        this.eventSource.addEventListener("message", this.updateModifiedCalibrationData);
    }

    updateModifiedCalibrationData = async (e) => {
        this.setState({
            modifiedCalibrationData: JSON.parse(e.data).dataModified
        })
    };

    mercureLogin = async () => {
        const mercureToken = await UserController.mercureLogin();
        Cookies.set("mercureToken", mercureToken);
    };

    //project public methods

    async setMapModified(mapId, table) {
        try {
            const isModified = await ProjectMapController.setMapModified(mapId, table);
            const folderStructure = FolderStructure.setMapModified(mapId, isModified, this.state.folderStructure);

            const loadedMaps = {...this.state.loadedMaps};
            loadedMaps[mapId].modified = await ProjectMapController.getMapModified(mapId);
            loadedMaps[mapId].swModified = isModified;
            this.setState({folderStructure, loadedMaps});
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
            this.alert("Something went wrong", "danger")
        }
    };

    async setMapModifiedAxis(mapId, axisId, modifiedAxisData) {
        try {
            const [modifiedMaps, isModified] = await ProjectMapController.setMapModifiedAxis(mapId, axisId, modifiedAxisData);

            const folderStructure = modifiedMaps.reduce((folderStructure, mapId) => {
                return FolderStructure.setMapModified(mapId, isModified, folderStructure)
            }, this.state.folderStructure); //todo: make a method for this in FolderStructure.js

            const loadedMaps = {...this.state.loadedMaps}
            loadedMaps[mapId].modified = await ProjectMapController.getMapModified(mapId);

            if (modifiedMaps.length > 1) this.alert(`${modifiedMaps.length-1} maps other than this one were affected - check edited maps under 'My Maps'`, "danger");

            this.setState({folderStructure, loadedMaps});
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
            this.alert("Something went wrong", "danger")
        }
    };

    async toggleMapFavourited(mapId) {
        try {
            await ProjectMapController.toggleMapFavourited(mapId)
            const folderStructure = FolderStructure.toggleMapFavourited(mapId, this.state.folderStructure);

            const loadedMaps = {...this.state.loadedMaps}
            loadedMaps[mapId] = await ProjectMapController.getMap(mapId);

            this.setState({folderStructure, loadedMaps});
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
            this.alert("Something went wrong", "danger")
        }
    };

    async undo(mapId) {
        try {
            const isModified = await ProjectMapController.undoMapByOneVersion(mapId);
            const folderStructure = FolderStructure.setMapModified(mapId, isModified, this.state.folderStructure);

            const loadedMaps = {...this.state.loadedMaps}
            loadedMaps[mapId].modified = await ProjectMapController.getMapModified(mapId);

            this.setState({folderStructure, loadedMaps});
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
            this.alert("Something went wrong", "danger")
        }
    }

    //UI public methods

    updateWindowPos(id, x, y) {
        const windows = Windows.updateWindowPos(id, x, y, this.state.windows);

        if(!equals(windows, this.state.windows)){
            UIController.setWindows(windows, projectId);
            this.setState({windows});
        }
    }

    activateAxisEditing(mapId, axisName) {
        if (axisName !== "xAxis" && axisName !== "yAxis") throw new Error(`Invalid Axis Name: ${axisName}`)
        const windows = Windows.activateAxisEditing(mapId, axisName, this.state.windows);

        UIController.setWindows(windows, projectId);
        this.setState({windows});
    }

    deactivateAxisEditing(mapId) {
        const windows = Windows.deactivateAxisEditing(mapId, this.state.windows);

        UIController.setWindows(windows, projectId);
        this.setState({windows});
    }

    async openWindow(mapId) {
        try {
            const alreadyOpen = this.state.windows.some(window => window.mapId === mapId);

            if(!alreadyOpen && (this.state.windows.length < 9)){
                const windows = Windows.openWindow(mapId, this.state.windows);

                UIController.setWindows(windows, projectId);
                this.setState({windows});

                this.state.loadedMaps[mapId] = await ProjectMapController.getMap(mapId);
                this.setState({loadedMaps: this.state.loadedMaps});
            }
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
            this.closeWindow(mapId);
            this.alert("Something went wrong", "danger")
        }
    }

    closeWindow(mapId) {
        const windows = this.state.windows.filter(window => window.mapId !== mapId);

        UIController.setWindows(windows, projectId);
        this.setState({windows});
    }

    getSelectedWindow() {
        return Windows.getFrontWindow(this.state.windows);
    }

    setSelectedWindow(mapId) {
        const windows = Windows.bringWindowToFront(mapId, this.state.windows);

        UIController.setWindows(windows, projectId);
        this.setState({windows});
    }

    async _checkNetworkConnection() {
        const apiConnected = await ping();

        if (apiConnected && !this.state.networkConnected){
            this.alert("Network connection restored.", "Success");
            this.setState({networkConnected: true, blackout: false})
        }

        if (!apiConnected) this.setState({networkConnected: false, blackout: true});
    }

    confirmDisclaimer() {
        UIController.confirmDisclaimer(projectId);
        const disclaimerConfirmed = UIController.isDisclaimerConfirmed(projectId);
        this.setState({disclaimerConfirmed})
    }

    toggleFullScreen() {
        this.setState({fullScreen: !this.state.fullScreen});
    }

    setFullScreen(fullScreen) {
        this.setState({fullScreen});
    }

    /**
     * Creates a new alert toast
     * @param {string} message - the message to display
     * @param {string} bgColour - bg colour for toast 'Brand', 'Success', 'Neutral' and 'Danger'
     */
    alert(message, bgColour) {
        this._setToast(message, bgColour, "alert", 10000)
    }

    /**
     * Creates a new confirm toast
     * @param {string} message - the message to display
     * @param {string} bgColour - bg colour for toast 'Brand', 'Success', 'Neutral' and 'Danger'
     * @param {boolean} [cancelable] - should there be a cancel button
     * @return {Promise} success - promise that resolves on success
     */
    confirm(message, bgColour, cancelable=true) {
        return new Promise((resolve) => {
            this._setToast(message, bgColour, "confirm", 0, cancelable, success => {
                resolve(success)
            });
        });
    }

    /**
     * Sets a new toast
     * @param {string} message - the message to display
     * @param {string} type - 'confirm' or 'alert'. Alert displays message, confirm displays message and ok button
     * @param {string} bgColour - bg colour for toast 'Brand', 'Success', 'Neutral' and 'Danger'
     * @param {number} timeout - any number other than 0 will provide a timeout time for toast (in ms). If no timeout provided, toast will not timeout.
     * @param {boolean} [cancelable] - for type 'confirm' should there be a cancel button
     * @param {function} [callBack] - callback function that describes an action. Params ({bool} success - true if ok, false if cancel)
     */
    _setToast(message, bgColour, type, timeout, cancelable=true, callBack=null) {
        this.setState(prevState => {
            const toasts = prevState.toasts.filter(toast => toast.message !== message);

            bgColour = bgColour.charAt(0).toUpperCase() + bgColour.slice(1).toLowerCase(); //sanitise bgColour

            const id = shortid.generate();

            if (timeout !== 0) window.setTimeout(() => this.unsetToast(id), timeout);

            return {toasts: [...toasts, {id, message, type, bgColour, cancelable, callBack}]}
        });
    }

    /**
     * Unsets the current toast
     * @param id - index of the new toast
     */
    unsetToast(id) {
        this.setState(prevState => {
            const toasts = prevState.toasts.filter(toast => toast.id !== id);
            return {...prevState, toasts}
        });
    }

    async reportMapIssue(mapId, message) {
        const map = this.state.loadedMaps[mapId];
        const projectId = this.state.id;

        if(!map) {
            this.alert("Map not found. Failed to report map, please try again.", "danger");
            return;
        }

        let finalMessage = "";
        let mapIdString = `Reported Map Id: ${mapId}`
        finalMessage = `${mapIdString}\n\n${message}`

        try {
            await TicketController.createMapReportTicket(map.name, projectId, finalMessage);
            this.alert("Thank you for reporting this map, one of our engineers will be in touch soon.", "success");
        } catch (error) {
            this.alert("Failed to report map, please try again.", "danger");
        }
    };

    render() {
        return (
            <Context.Provider value={this.state}>
                {this.props.children}
            </Context.Provider>
        );
    }
}